import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Avatar,
  Badge,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import {
  Notifications,
  AccountCircle,
  Person,
  Settings,
  Logout,
  NotificationsActive,
  Circle,
  Schedule,
  CheckCircle,
  Clear,
} from "@mui/icons-material";
import { ensureSubscriptionAndSendToBackend, unsubscribePush } from "../push/pushClient";
import api from "../api/api";
import en_ja from "../en-ja-map";

const Header = ({ userName = "Admin Sarah" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const BACKEND_SUB_URL = `${API_BASE}/v1/notification/notification-subscription`;
  const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  const userEmail = user?.email || "";
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const [notifMsg, setNotifMsg] = useState("");
  const [notifOn, setNotifOn] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  useEffect(() => {
    if (userEmail) loadNotifications();
  }, [userEmail]);

  useEffect(() => {
    const handler = (evt) => {
      const msg = evt?.data ?? evt;
      if (msg?.type === 'NEW_NOTIFICATION') {
        // Either optimistically insert msg.payload, or just refetch
        loadNotifications();
      }
    };

    let bc;
    if ('BroadcastChannel' in window) {
      bc = new BroadcastChannel('push-events');
      bc.addEventListener('message', handler);
    }
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', handler);
    }

    return () => {
      if (bc) bc.close();
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', handler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const handleEnablePush = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setNotifMsg("");
    try {
      await ensureSubscriptionAndSendToBackend({
        vapidPublicKey: VAPID_PUBLIC_KEY,
        backendUrl: BACKEND_SUB_URL,
        userEmail,
      });
      setNotifOn(true);
      setNotifMsg("Notifications enabled");
    } catch (err) {
      setNotifOn(false);
      setNotifMsg(
        Notification?.permission === "denied"
          ? "Blocked in browser settings"
          : err?.message || "Failed to enable"
      );
    }
  };

  const loadNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const { data } = await api.post("/v1/notification/get/my-notifications", { email: userEmail });
      console.log("Fetched notifications:", data);
      const items = Array.isArray(data?.notifications) ? data.notifications : (Array.isArray(data) ? data : []);
      setNotifications(items);
      setUnreadCount(items.filter(n => !n.read).length);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post("/v1/notification/mark-as-read", { id: notificationId });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post("/v1/notification/mark-all-as-read", { email: userEmail });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const clearNotification = async (notificationId) => {
    try {
      await api.delete(`/v1/notification/delete/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notifications.find(n => n.id === notificationId && !n.read)) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const handleDisablePush = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { await unsubscribePush(); setNotifOn(false); setNotifMsg("Notifications disabled"); }
    catch (err) { setNotifMsg(err?.message || "Failed to disable"); }
  };

  const handleBellClick = async (e) => {
    setNotifAnchorEl(e.currentTarget);
    try {
      await handleEnablePush(e);
      await loadNotifications();
    } catch (err) {
      console.error("Error in bell click:", err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    handleNotifMenuClose();
    if (notification.notification_title?.toLowerCase().includes('appointment') ||
      notification.notification_body?.toLowerCase().includes('appointment')) {
      navigate('/booking-management');
    }
  };

  const handleNotifMenuClose = () => setNotifAnchorEl(null);

  const handleProfileClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setProfileAnchorEl(null);
  };

  const handleProfileMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleProfileClose();
    navigate("/profile");
  };

  const handleSettingsClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleProfileClose();
    navigate("/settings");
  };

  const handleLogout = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleProfileClose();
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.clear();
    navigate("/login");
  };

  const formatNotificationTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? "Just now" : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const formatNotificationBody = (body) => {
    const appointmentMatch = body.match(/Appointment on (.+?) at (.+?) \((.+?)\)\. Patient: (.+?) - (.+)/);
    if (appointmentMatch) {
      const [, date, time, type, patientName, email] = appointmentMatch;
      const formattedDate = new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const formattedTime = time.substring(0, 5);
      return `${formattedDate} at ${formattedTime} (${type}) - ${patientName}`;
    }
    return body.length > 80 ? body.substring(0, 80) + '...' : body;
  };

  const getNotificationIcon = (notificationTitle) => {
    if (notificationTitle?.toLowerCase().includes("appointment")) {
      return <Schedule sx={{ fontSize: 18, color: "#76AEDE" }} />;
    }
    return <NotificationsActive sx={{ fontSize: 18, color: "#76AEDE" }} />;
  };

  const getPageTitle = () => {
    const pathname = location.pathname;
    const route = pathname.split("/")[1];
    if (!route) return { title: "", subTitle: "" };
    switch (route) {
      case "patient-management":
        return {
          title: en_ja["Patient Information"],
          subTitle: en_ja["Manage and View all Patient information"],
        };
      case "appointment":
        return {
          title: en_ja["Appointments & Slots"],
          subTitle: en_ja["Manage and View all Appointments and Slots"],
        };
      case "billings-payments":
        return {
          title: en_ja["Billing & Payments"],
          subTitle: en_ja["Manage and View all Billing and Payments"],
        };
      case "booking-management":
        return {
          title: en_ja["Bookings"],
          subTitle: en_ja["Manage and View all Bookings"],
        };
      case "connect-call":
        return {
          title: "Connect",
          subTitle: "Here you can connect with the patient",
        };
      case "doctor-management":
        return {
          title: en_ja["Doctors"],
          subTitle: en_ja["Manage and View all Doctors associated with your clinic"],
        };
      case "prescription-management":
        return {
          title: en_ja['Prescriptions'],
          subTitle: en_ja["Here you can manage all prescriptions"],
        };
      case "settings":
        return {
          title: en_ja["Settings"],
          subTitle: en_ja["Manage and update your account settings"],
        };
      case "profile":
        return {
          title: en_ja["Profile"],
          subTitle: en_ja["Manage and view your profile information"],
        };
      default:
        return {
          title: "Clinic Management",
          subTitle: "",
        };
    }
  };

  const pageTitle = getPageTitle();

  const getCurrentDateTime = () => {
    const now = new Date();
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    const date = now.toLocaleDateString("en-US", options);
    const time = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { date, time };
  };

  const { date, time } = getCurrentDateTime();

  return (
    <>
      {isDashboard ? (
        <Box
          sx={{
            backgroundColor: "#f9f9f9",
            py: 4,
            pl: 2,
            px: 4,
            mx: 2,
            borderRadius: "32px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)",
            position: "relative",
            zIndex: 10,
            background: "linear-gradient(145deg, #f9f9f9 0%, #f5f5f5 100%)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ ml: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#2c2c2c",
                    mb: 0.5,
                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                    letterSpacing: "-0.02em",
                  }}
                >
                  {en_ja["Welcome, {name}!"].replace("{name}", user?.clinic_name || "")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#6b7280", fontSize: "0.9rem", fontWeight: 400 }}
                >
                  {en_ja["Here's Your Clinic Management Overview For Today"]}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  padding: "8px 12px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, rgba(118, 174, 222, 0.08) 0%, rgba(161, 195, 232, 0.12) 100%)",
                  border: "1px solid rgba(118, 174, 222, 0.1)",
                }}
              >
                <IconButton
                  onClick={handleBellClick}
                  sx={{
                    color: "#6b7280",
                    background: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "10px",
                    padding: "8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(118, 174, 222, 0.1)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(118, 174, 222, 0.2)",
                      color: "#76AEDE",
                    },
                  }}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <Notifications />
                  </Badge>
                </IconButton>
                <IconButton
                  onClick={handleProfileClick}
                  sx={{
                    p: 0,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "transparent",
                      transform: "translateY(-2px)",
                      "& .MuiAvatar-root": {
                        boxShadow: "0 6px 20px rgba(118, 174, 222, 0.3)",
                        transform: "scale(1.05)",
                      },
                    },
                  }}
                >
                  <Avatar
                    {...(user?.profile_image && { src: user.profile_image })}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: "#76AEDE",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      boxShadow: "0 3px 12px rgba(118, 174, 222, 0.2)",
                      border: "2px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {!user?.profile_image && <AccountCircle />}
                  </Avatar>
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "#f9f9f9",
            py: 3,
            px: 4,
            mx: 2,
            mt: 1,
            borderRadius: "32px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)",
            background: "linear-gradient(145deg, #f9f9f9 0%, #f5f5f5 100%)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            position: "relative",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ ml: 2 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#000",
                    fontWeight: "bold",
                    mb: 0.5,
                    fontSize: "32px",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {pageTitle.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#666666", fontSize: "16px" }}
                >
                  {pageTitle.subTitle}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                padding: "8px 16px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, rgba(118, 174, 222, 0.08) 0%, rgba(161, 195, 232, 0.12) 100%)",
                border: "1px solid rgba(118, 174, 222, 0.1)",
              }}
            >
              <IconButton
                onClick={handleBellClick}
                sx={{
                  color: "#6b7280",
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "10px",
                  padding: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(118, 174, 222, 0.1)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(118, 174, 222, 0.2)",
                    color: "#76AEDE",
                  },
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
              <IconButton
                onClick={handleProfileClick}
                sx={{
                  p: 0,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "transparent",
                    transform: "translateY(-2px)",
                    "& .MuiAvatar-root": {
                      boxShadow: "0 6px 20px rgba(118, 174, 222, 0.3)",
                      transform: "scale(1.05)",
                    },
                  },
                }}
              >
                <Avatar
                  {...(user?.profile_image && { src: user.profile_image })}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: "#76AEDE",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 3px 12px rgba(118, 174, 222, 0.2)",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {!user?.profile_image && <AccountCircle />}
                </Avatar>
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      <Menu
        anchorEl={profileAnchorEl}
        open={isProfileMenuOpen}
        onClose={handleProfileClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            borderRadius: "12px",
            minWidth: 180,
            zIndex: 9999,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        sx={{ zIndex: 9999 }}
      >
        <MenuItem
          onClick={handleProfileMenuClick}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(118, 174, 222, 0.08)",
            },
          }}
        >
          <ListItemIcon>
            <Person fontSize="small" sx={{ color: "#76AEDE" }} />
          </ListItemIcon>
          <ListItemText>{en_ja["Profile"]}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleSettingsClick}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(118, 174, 222, 0.08)",
            },
          }}
        >
          <ListItemIcon>
            <Settings fontSize="small" sx={{ color: "#76AEDE" }} />
          </ListItemIcon>
          <ListItemText>{en_ja["Settings"]}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          sx={{
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.08)",
            },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: "#f44336" }} />
          </ListItemIcon>
          <ListItemText sx={{ color: "#f44336" }}>{en_ja["Logout"]}</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={handleNotifMenuClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            borderRadius: "16px",
            minWidth: 400,
            maxWidth: 450,
            maxHeight: 500,
            overflow: "hidden",
            border: "1px solid rgba(118, 174, 222, 0.1)",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          }
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #f0f0f0", background: "linear-gradient(135deg, rgba(118, 174, 222, 0.05) 0%, rgba(161, 195, 232, 0.08) 100%)" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" fontWeight="600" sx={{ color: "#2c2c2c" }}>
              {en_ja["Notifications"]}
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} ${en_ja['new']}`}
                size="small"
                sx={{
                  backgroundColor: "#76AEDE",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.75rem"
                }}
              />
            )}
          </Box>
          {/* notifications.length > 0 && (
<Stack direction="row" spacing={1}>
{unreadCount > 0 && (
<Button
size="small"
startIcon={<CheckCircle sx={{ fontSize: "16px !important" }} />}
onClick={markAllAsRead}
sx={{
fontSize: "0.75rem",
color: "#76AEDE",
textTransform: "none",
fontWeight: 500,
minWidth: "auto",
px: 1.5,
py: 0.5,
"&:hover": { backgroundColor: "rgba(118, 174, 222, 0.1)" }
}}
>
Mark all read
</Button>
)}
<Button
size="small"
onClick={() => navigate("/notifications")}
sx={{
fontSize: "0.75rem",
color: "#6b7280",
textTransform: "none",
fontWeight: 500,
minWidth: "auto",
px: 1.5,
py: 0.5,
"&:hover": { backgroundColor: "rgba(107, 114, 128, 0.1)" }
}}
>
View all
</Button>
</Stack>
) */}
        </Box>

        <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
          {notificationsLoading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {en_ja["Loading notifications..."]}
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Notifications sx={{ fontSize: 48, color: "#e0e0e0", mb: 2 }} />
              <Typography variant="body1" color="text.secondary" fontWeight="500" sx={{ mb: 1 }}>
                {en_ja["No notifications yet"]}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                {en_ja["We'll notify you when something important happens"]}
              </Typography>
            </Box>
          ) : (
            notifications.slice(0, 10).map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid #f8f9fa",
                  backgroundColor: notification.read ? "transparent" : "rgba(118, 174, 222, 0.02)",
                  "&:hover": {
                    backgroundColor: notification.read ? "rgba(0, 0, 0, 0.04)" : "rgba(118, 174, 222, 0.08)",
                  },
                  "&:last-child": { borderBottom: "none" }
                }}
              >
                <ListItemIcon sx={{ minWidth: 35 }}>
                  {getNotificationIcon(notification.notification_title)}
                </ListItemIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: notification.read ? 400 : 600,
                        color: notification.read ? "#6b7280" : "#2c2c2c",
                        fontSize: "0.85rem",
                        lineHeight: 1.3,
                        flex: 1,
                        mr: 1
                      }}
                    >
                      {notification.notification_title}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {!notification.read && (
                        <Circle sx={{ fontSize: 8, color: "#76AEDE" }} />
                      )}
                      {/* <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearNotification(notification.id);
                        }}
                        sx={{
                          p: 0.5,
                          color: "#9ca3af",
                          "&:hover": {
                            color: "#f44336",
                            backgroundColor: "rgba(244, 67, 54, 0.1)"
                          }
                        }}
                      >
                        <Clear sx={{ fontSize: 14 }} />
                      </IconButton> */}
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#6b7280",
                      fontSize: "0.8rem",
                      lineHeight: 1.4,
                      mb: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {formatNotificationBody(notification.notification_body)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      fontSize: "0.7rem",
                      fontWeight: 500
                    }}
                  >
                    {formatNotificationTime(notification.created_at)}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          )}
        </Box>
      </Menu>
    </>
  );
};

export default Header;